import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import onboardingTemplateService from '../../services/onboardingTemplates/onboardingTemplateService';
import updateOnboardingTemplateService from '../../services/onboardingTemplates/updateOnboardingTemplateService';
import OnboardingTemplateForm from './OnboardingTemplateForm';
import { COMMUNITY_DETAIL_TABS } from '../../constants';
import { mapFormToOnboardingTemplate } from '../../helpers/mapFormToOnboardingTemplate';
import { mapOnboardingTemplateToForm } from '../../helpers/mapOnboardingTemplateToForm';

const EditOnboardingTemplate = ({
  open,
  dispatchUpdateOnboardingTemplatePut,
  updateOnboardingTemplateFetch,
  dispatchOnboardingTemplateGet,
  onboardingTemplateFetch,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, actionId } = useParams();

  const handleSubmit = useCallback(
    body =>
      dispatchUpdateOnboardingTemplatePut(
        actionId,
        mapFormToOnboardingTemplate(body, id),
        () => navigate(`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.ONBOARDING_TEMPLATES}`),
        undefined,
        t
      ),
    [id, actionId, dispatchUpdateOnboardingTemplatePut, t, navigate]
  );

  useEffect(() => {
    if (open && id) {
      dispatchOnboardingTemplateGet(
        actionId,
        () => navigate(`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.ONBOARDING_TEMPLATES}`),
        t
      );
    }
  }, [id, actionId, open, t, dispatchOnboardingTemplateGet, navigate]);

  const defaultData = useMemo(
    () =>
      !onboardingTemplateFetch.pending &&
      mapOnboardingTemplateToForm(onboardingTemplateFetch.value),
    [onboardingTemplateFetch]
  );

  if (!open || !defaultData?.name) return null;

  return (
    <OnboardingTemplateForm
      header={onboardingTemplateFetch.value?.name}
      onSubmit={handleSubmit}
      submitProcessing={updateOnboardingTemplateFetch.pending}
      returnRoute={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.ONBOARDING_TEMPLATES}`}
      cancelRoute={`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.ONBOARDING_TEMPLATES}`}
      dataLoading={onboardingTemplateFetch.pending}
      data={defaultData}
      showRemoveButton
      editMode
    />
  );
};

EditOnboardingTemplate.propTypes = {
  dispatchUpdateOnboardingTemplatePut: PropTypes.func.isRequired,
  updateOnboardingTemplateFetch: PropTypes.object.isRequired,
  dispatchOnboardingTemplateGet: PropTypes.func.isRequired,
  onboardingTemplateFetch: PropTypes.object.isRequired,
  open: PropTypes.bool,
};

EditOnboardingTemplate.defaultProps = { open: false };

export default compose(
  onboardingTemplateService,
  updateOnboardingTemplateService
)(EditOnboardingTemplate);
